import {
  DELIVERY_DEFAULT_SETTINGS,
  IDeliverySettings,
  CART_DEFAULT_SETTINGS,
  EServiceGroupName,
  ICartSettings,
  IPaymentSettings,
  PAYMENT_DEFAULT_SETTINGS,
  BENEFITS_DEFAULT_SETTINGS,
  IBenefitCountryOptions,
} from '@model';
import { ensureObjectIsDate } from '@utils/date';

import { IGiftPackagingBox, IGiftPackagingContent } from '../components';
import { ECartPageFeatures, IBenefits, ICartPageSettings } from '../model';

import { ICartPageSettingsGatewayResponse } from './cart';
import { IGiftPackagingContentGatewayDto } from './giftPackaging/model';
import { IGatewayBenefits } from './model';
import {
  IBenefitSettingsResponse,
  ICartSettingsResponse,
  IDeliverySettingsResponse,
  IPaymentSettingsResponse,
} from './model';

export const transformCartSettings = (data: ICartSettingsResponse): ICartSettings => {
  if (!data) {
    return CART_DEFAULT_SETTINGS;
  }

  const cartSettings = {
    ...CART_DEFAULT_SETTINGS,
    giftPackaging: {
      ...CART_DEFAULT_SETTINGS.giftPackaging,
      giftPackagingTypes: data.serviceSettings.filter(
        (x) => x.groupName === EServiceGroupName.GIFT_PACKAGING && x.isActive,
      ),
    },
  };

  cartSettings.giftPackagingPrice =
    data.serviceSettings.find((x) => x.groupName === EServiceGroupName.GIFT_PACKAGING)?.price || 0;
  cartSettings.ecoPackagingPrice =
    data.serviceSettings.find((x) => x.groupName === EServiceGroupName.ECO_PACKAGING)?.price || 0;
  cartSettings.insurancePrice =
    data.serviceSettings.find((x) => x.groupName === EServiceGroupName.INSURANCE)?.price || 0;

  const discoveryBoxSettings = data.serviceSettings.find((x) => x.groupName === EServiceGroupName.DISCOVERY_BOX);

  if (discoveryBoxSettings) {
    cartSettings.discoveryBox = {
      image: discoveryBoxSettings.image,
    };
  }

  const fundRaisingSettings = data.serviceSettings.find((x) => x.groupName === EServiceGroupName.FUND_RAISING);

  if (fundRaisingSettings) {
    cartSettings.fundRaising = {
      active: fundRaisingSettings.isActive,
      price: fundRaisingSettings.price,
      image: fundRaisingSettings.image,
    };
  }

  data.settings.forEach((setting) => {
    switch (setting.name) {
      case ECartPageFeatures.ECO_PACKAGING_ON.toString():
        cartSettings.ecoPackagingEnabled = Boolean(Number(setting.value));
        break;
      case ECartPageFeatures.GIFT_PACKAGING_MODAL_ON.toString():
        cartSettings.giftPackaging.giftPackagingOn = Boolean(Number(setting.value));
        break;
      case ECartPageFeatures.GIFT_PACKAGING_GIFT_CARD_ON.toString():
        cartSettings.giftPackaging.giftCardEnabled = Boolean(Number(setting.value));
        break;
      case ECartPageFeatures.GIFT_PACKAGING_GIFT_CARD_TEXT_LIMIT.toString():
        cartSettings.giftPackaging.giftCardTextLimit = Number(setting.value);
        break;
      case ECartPageFeatures.GIFT_PACKAGING_MAX_BOXES.toString():
        cartSettings.giftPackaging.maxBoxes = Number(setting.value);
        break;
      case ECartPageFeatures.GIFT_PACKAGING_MAX_PRODUCTS.toString():
        cartSettings.giftPackaging.maxProductInBox = Number(setting.value);
        break;
      case ECartPageFeatures.GIFT_PACKAGING_URL.toString():
        cartSettings.giftPackagingLinkUrl = setting.value;
        break;
      case ECartPageFeatures.LOYALTY_MODAL_IMAGE_URL.toString():
        cartSettings.loyaltyModalImageUrl = setting.value;
        break;
      case ECartPageFeatures.SHOW_GIFT_SELECTION_BY_DEFAULT.toString():
        cartSettings.showGiftSelectionByDefault = Boolean(Number(setting.value));
        break;
      case ECartPageFeatures.INSURANCE_ENABLED.toString():
        cartSettings.insuranceEnabled = Boolean(Number(setting.value));
        break;
      case ECartPageFeatures.INSURANCE_URL.toString():
        cartSettings.insuranceLinkUrl = setting.value;
        break;
      case ECartPageFeatures.VOUCHER_UNDER_PRODUCT_ENABLED.toString():
        cartSettings.voucherUnderProductEnabled = Boolean(Number(setting.value));
        break;
      default:
        break;
    }
  });

  return cartSettings;
};

export const transformDeliverySettings = (data: IDeliverySettingsResponse): IDeliverySettings => {
  if (!data) {
    return DELIVERY_DEFAULT_SETTINGS;
  }

  const deliverySettings: IDeliverySettings = {
    ...DELIVERY_DEFAULT_SETTINGS,
    ...data,
    freeDelivery: data.freeDeliveryEnabled,
  };

  data.deliveryFeatures.forEach((setting) => {
    switch (setting.name) {
      case ECartPageFeatures.REPEAT_LAST_ORDER.toString():
        deliverySettings.orderAsLastTimeEnabled = Boolean(Number(setting.value));
        break;
      default:
        break;
    }
  });

  if (data.deliveryType !== null && data.deliveryType.length > 0) {
    const priceLimits = data.deliveryType.filter((x) => x.priceLimit > 0).map((x) => x.priceLimit);

    if (priceLimits.length > 0) {
      const minimalPriceLimit = Math.min(...priceLimits);

      deliverySettings.minimalPriceLimit = minimalPriceLimit;
    }
  }

  return deliverySettings;
};

export const transformPaymentSettings = (data: IPaymentSettingsResponse): IPaymentSettings => {
  if (!data) {
    return PAYMENT_DEFAULT_SETTINGS;
  }

  return {
    ...PAYMENT_DEFAULT_SETTINGS,
    paypal: { ...data.payPal },
    adyen: { ...data.adyen },
  };
};

export const transformCartPageSettings = (
  data: ICartPageSettingsGatewayResponse,
  currentCountry: string,
): ICartPageSettings => ({
  ...transformDeliverySettings(data.aggregatedConfiguration.deliveryConfiguration.data),
  ...transformCartSettings(data.aggregatedConfiguration.cartConfiguration.data),
  ...transformPaymentSettings(data.aggregatedConfiguration.paymentConfiguration.data),
  ...transformBenefitSettings(data.aggregatedConfiguration.benefitConfiguration.data, currentCountry),
});

export const transformGiftPackagingContentFromDto = (data: IGiftPackagingContentGatewayDto): IGiftPackagingContent => {
  const boxes = data.box.map((box) => {
    return {
      ident: box.ident,
      type: box.type,
      operation: box.operation,
      isFreeGiftPackaging: box.isFreeGiftPackaging,
      isCampaignFreeBox: box.isCampaignFreeBox,
      card: box.card ? { ...box.card, disabled: false } : { disabled: true, text: '', color: '' },
      productCode: box.productCode,
    } as IGiftPackagingBox;
  });

  return {
    ...data,
    box: boxes,
  };
};

export const transformGiftPackagingContentToDto = (data: IGiftPackagingContent): IGiftPackagingContentGatewayDto => {
  const boxes = data.box.map((box) => {
    return {
      ident: box.ident,
      type: box.type,
      operation: box.operation,
      isFreeGiftPackaging: box.isFreeGiftPackaging,
      isCampaignFreeBox: box.isCampaignFreeBox,
      card: box.card.disabled ? null : { text: box.card.text, color: box.card.color },
      productCode: box.productCode,
    } as IGiftPackagingBox;
  });

  return {
    ...data,
    box: boxes,
  };
};

const transformBenefitSettings = (
  response: IBenefitSettingsResponse,
  currentCountry: string,
): IBenefitCountryOptions => {
  if (!response) {
    return BENEFITS_DEFAULT_SETTINGS;
  }

  return {
    ...response.freeDeliverySettings.countryOptions[0],
    isDeliveryBenefitEnabled: response.freeDeliverySettings.supportedCountries.includes(currentCountry),
  };
};

export const transformCartBenefits = (response: IGatewayBenefits | null | undefined): IBenefits => ({
  loyalties:
    response?.loyalty?.loyalties.map((benefit) => ({
      ...benefit,
      ...(benefit.expirationDate && { expirationDate: ensureObjectIsDate(benefit.expirationDate) }),
      ...(benefit.validUntil && { validUntil: ensureObjectIsDate(benefit.validUntil) }),
    })) ?? [],
  vouchers: response?.vouchers ?? [],
});
